import React, { FunctionComponent, useMemo } from 'react';
import { Typography, Box } from '@material-ui/core';
import { startCase, isObject, entries } from 'lodash';

import Checkbox from 'application/components/checkbox';

import SettingInput from './setting-input';

type SettingsInputRow<T> = {
  key: string;
  value: T;
  onChange: (value: T) => void;
  metaData: { default: T; options?: string[] };
  selectedForModifier?: boolean;
};

type SettingsInputTableProps = {
  rows: SettingsInputRow<number | boolean | object | string | undefined>[];
  mode: 'profile' | 'modifier';
  onChangeSettingSelected?: (key: string, selected: boolean) => void;

  /** Shows an outer border around the table. Defaults to true. */
  withBorder?: boolean;
};

type FlatRow = {
  rowKey: string;
  col1: string;
  col2: string;
  col3: string;
  showCol1: boolean;
  showCol2: boolean;
  value: number | boolean | string;
  onChange: (v: any) => void;
  defaultValue: number | boolean | string | undefined;
  options?: string[];
  selectedForModifier?: boolean;
  topLevelKey: string;
  groupIndex: number;
};

function buildFlatRows(rows: SettingsInputRow<number | boolean | object | string | undefined>[]): FlatRow[] {
  const flat: FlatRow[] = [];

  rows.forEach(({ key, value, onChange, metaData, selectedForModifier }, groupIndex) => {
    if (!isObject(value)) {
      flat.push({
        rowKey: key,
        col1: startCase(key),
        col2: '',
        col3: '',
        showCol1: true,
        showCol2: false,
        value: value as any,
        onChange: onChange as any,
        defaultValue: metaData.default as any,
        options: metaData.options,
        selectedForModifier,
        topLevelKey: key,
        groupIndex,
      });
    } else {
      let firstInGroup = true;
      entries(value as object).forEach(([k, v]) => {
        if (!isObject(v)) {
          flat.push({
            rowKey: `${key}-${k}`,
            col1: startCase(key),
            col2: startCase(k),
            col3: '',
            showCol1: firstInGroup,
            showCol2: true,
            value: v as any,
            onChange: (newVal: any) => onChange({ ...(value as object), [k]: newVal }),
            defaultValue: (metaData.default as any)?.[k],
            options: metaData.options,
            selectedForModifier,
            topLevelKey: key,
            groupIndex,
          });
          firstInGroup = false;
        } else {
          let firstInSubGroup = true;
          entries(v as object).forEach(([kk, vv]) => {
            flat.push({
              rowKey: `${key}-${k}-${kk}`,
              col1: startCase(key),
              col2: startCase(k),
              col3: startCase(kk),
              showCol1: firstInGroup,
              showCol2: firstInSubGroup,
              value: vv as any,
              onChange: (newVal: any) =>
                onChange({ ...(value as object), [k]: { ...(v as object), [kk]: newVal } }),
              defaultValue: (metaData.default as any)?.[k]?.[kk],
              options: metaData.options,
              selectedForModifier,
              topLevelKey: key,
              groupIndex,
            });
            firstInGroup = false;
            firstInSubGroup = false;
          });
        }
      });
    }
  });

  return flat;
}

const COLUMN_TEMPLATE = '1fr 1fr 1fr 120px 170px';

const ColCell: FunctionComponent<{ hasContent?: boolean; px?: number }> = ({
  hasContent = false,
  px = 2,
  children,
}) => (
  <Box
    display="flex"
    alignItems="center"
    px={px}
    style={{ minWidth: 0, borderLeft: hasContent ? '1px solid #e2e8f0' : undefined }}
  >
    {children}
  </Box>
);

const SettingsInputTable: FunctionComponent<SettingsInputTableProps> = ({
  mode,
  rows,
  onChangeSettingSelected,
  withBorder = true,
}) => {
  const flatRows = useMemo(() => buildFlatRows(rows), [rows]);

  return (
    <Box style={{ border: withBorder ? '1px solid #cbd5e1' : undefined }}>
      {flatRows.map((row) => {
        const isOpaque = mode === 'modifier' && !row.selectedForModifier;
        const bg = row.groupIndex % 2 === 0 ? '#f8fafc' : undefined;
        const hasDefault = row.defaultValue !== undefined && row.defaultValue !== null;

        return (
          <Box
            key={row.rowKey}
            display="grid"
            style={{
              gridTemplateColumns: COLUMN_TEMPLATE,
              backgroundColor: bg,
              borderBottom: '1px solid #e2e8f0',
              opacity: isOpaque ? 0.5 : 1,
            }}
          >
            {/* Col 1 — top-level key or group name */}
            <ColCell hasContent={row.showCol1}>
              {row.showCol1 && (
                <Box display="flex" alignItems="center">
                  {mode === 'modifier' && (
                    <Checkbox
                      checked={row.selectedForModifier ?? true}
                      onChangeValue={(checked) => onChangeSettingSelected?.(row.topLevelKey, checked)}
                    />
                  )}
                  <Typography variant="body2" style={{ fontWeight: row.col2 ? 600 : 400 }}>
                    {row.col1}
                  </Typography>
                </Box>
              )}
            </ColCell>

            {/* Col 2 — sub-setting or sub-group name */}
            <ColCell hasContent={row.showCol2 && !!row.col2}>
              {row.showCol2 && row.col2 && (
                <Typography variant="body2" style={{ fontWeight: row.col3 ? 600 : 400 }}>
                  {row.col2}
                </Typography>
              )}
            </ColCell>

            {/* Col 3 — deep setting name */}
            <ColCell hasContent={!!row.col3}>
              {row.col3 && <Typography variant="body2">{row.col3}</Typography>}
            </ColCell>

            {/* Col 4 — default value */}
            <ColCell hasContent={hasDefault} px={1}>
              {hasDefault && (
                <Typography variant="caption" color="textSecondary">
                  Default: <strong>{row.defaultValue?.toString()}</strong>
                </Typography>
              )}
            </ColCell>

            {/* Col 5 — input */}
            <ColCell hasContent px={1}>
              <SettingInput
                value={row.value}
                onChange={row.onChange}
                defaultValue={row.defaultValue}
                options={row.options}
              />
            </ColCell>
          </Box>
        );
      })}
    </Box>
  );
};

export default SettingsInputTable;
